/* まんがのページ画像を押したら、画像だけを大きく出す + ページ送り。
 *
 * 2026-08-13: 2段階ズーム(全体 ↔ 幅いっぱい)で導入。
 * 2026-08-14: ユーザー指示で**ギャラリー化**した。
 *   ・フリック / 矢印ボタン / ← → キーで同じ話のページを行き来できる
 *     ★**まんがは右から左へ読む**ので、**左ボタン・← キー・右へのフリックが「次」**
 *       (2026-08-14 ユーザー指示。紙の右綴じの本と同じ向き)
 *   ・切り替えの合間に、ランダムで PR スライドを1枚はさむ
 *   ・開いている間は表示URLに ?img=<ページ番号> を付ける(アクセス計測用)
 *
 * ## 設計上の決めごと(理由つき)
 *
 * **ページ送りの範囲は「いま開いた話」だけ**。無限スクロールで複数の話が
 * 同じ DOM に載るが、話をまたいで送ると「第1話の3枚目の次が第2話の1枚目」になり、
 * 読者は自分がどの話にいるのか見失う。範囲は img の closest('[data-hta-entry]') で切る。
 *   ★例外: ルートに `data-gpm-gallery-all` がある記事(=「まんがだけ まとめて読む」)は
 *     **ページ全体**が1つのギャラリー。そこは話をまたいで読むことが目的のページ。
 *
 * **URL は history.replaceState + クエリ(?img=N)だけ**。
 *   ・無限スクロール(gp-manga.js)の ?n=&e= と同じ設計: サーバーは読まない・
 *     canonical はクリーンURLのまま(Shopify が未知クエリを落とす。月次の
 *     check_canonical.py が監視している)。SEO への副作用を作らない。
 *   ・pushState にしない: ページを3枚めくって戻るボタンを押すと、履歴を
 *     3回さかのぼることになり離脱できなくなる(gp-manga.js と同じ判断)。
 *   ・GA4 の拡張計測は履歴イベントで page_view を数えるので、これが
 *     「アクセスアップ施策」の実体になる。
 *   ・閉じたら img パラメータを**消す**。開いていない状態のURLに拡大表示の
 *     痕跡を残さない(共有されたとき n/e は意味を保つが img は文脈が無い)。
 *
 * **PR スライドはランダム・連続しない・URLを変えない**。
 *   ・確率は PR_CHANCE。乱数は Math.random(再現性は要らない。ここは演出)。
 *   ・PR の直後の切り替えでは出さない(2連続で出ると読む気が失せる)。
 *   ・PR 表示中にもう一度送る/戻すと、**元々行こうとしていた方向**へ抜ける。
 *   ・中身は <template data-gpm-lightbox-pr-template>(テーマの snippets/gp-manga-pr.liquid)。
 *     template が無ければ PR は一切出ない(fail-safe)。
 *
 * gp-manga.js とは独立。画像は無限スクロールで**あとから増える**ので、
 * 個々の img に listener を付けず、ルートで委譲して拾う。
 */
(function () {
  "use strict";

  var ROOT_SEL = "[data-gp-manga-article]";
  /* 対象はまんがのページ画像だけ。本文中の装飾画像まで拡大対象にしない。 */
  var IMAGE_SEL = ".gp-manga__page img";
  var ENTRY_SEL = "[data-hta-entry]";
  /* PR スライドを出す確率(ページ切り替え1回あたり)。1.0 にしない(毎回出ると読めない)。 */
  var PR_CHANCE = 0.3;
  /* フリック判定: 横に SWIPE_MIN px 以上動き、縦より横が明確に長いとき。 */
  var SWIPE_MIN = 48;
  var URL_PARAM = "img";
  /* スマホかどうか。CSS の 600px と**同じ値**にする(片方だけ変えない)。 */
  var MOBILE = window.matchMedia("(max-width: 600px)");

  var root = document.querySelector(ROOT_SEL);
  if (!root) return;
  /* ★読み方向(2026-08-29 追加): 既定は**右から左**(まんが単行本=右綴じ)。
     ルートに `data-gpm-ltr` がある面(trends のコラム記事の4コマ。コマ番号が
     左→右に振られている)だけ、キーとフリックの向きを反転する。
     属性が無い既存の gp-manga 記事は**従来どおり**動く(後方互換)。
     矢印ボタンの左右は CSS 側(gp-manga.css / tr-article-media.css)が決める。 */
  var LTR = root.hasAttribute("data-gpm-ltr");

  var box = root.querySelector("[data-gpm-lightbox]");
  var image = root.querySelector("[data-gpm-lightbox-img]");
  var caption = root.querySelector("[data-gpm-lightbox-caption]");
  var closeButton = root.querySelector("[data-gpm-lightbox-close]");
  if (!box || !image || !caption || !closeButton) return;
  /* ページ送りUI。古いテーマ(未反映)では無い。無ければ従来どおり単枚表示で動く。 */
  var prevButton = root.querySelector("[data-gpm-lightbox-prev]");
  var nextButton = root.querySelector("[data-gpm-lightbox-next]");
  var countLabel = root.querySelector("[data-gpm-lightbox-count]");
  var prBox = root.querySelector("[data-gpm-lightbox-pr-box]");
  var prTemplate = root.querySelector("[data-gpm-lightbox-pr-template]");

  var lastFocus = null;
  var savedOverflow = "";

  /* ── ギャラリーの状態 ──
     items   : いま開いている話のページ画像(開いた瞬間に収集。追記で増えても
               開いている間は固定 — 表示中に並びが変わるほうが混乱する)
     index   : いま見せている画像の位置
     prFrom / prTarget : PRスライドの前後の行き先(PR表示中だけ意味を持つ)
     lastWasPr         : 直前の切り替えがPRだったか(連続を防ぐ) */
  var items = [];
  var index = 0;
  var prShowing = false;
  var prFrom = 0;
  var prTarget = 0;
  var prDir = 1;      /* PRが出たときの進行方向(+1/-1)。脱出の向き判定に使う */
  var lastWasPr = false;

  function captionFor(img) {
    var figure = img.closest("figure");
    var text = figure ? figure.querySelector("figcaption") : null;
    /* figcaption は「1/3」のようなページ番号。読み上げには alt のほうが情報量がある。 */
    return [(text ? text.textContent : "").trim(), (img.getAttribute("alt") || "").trim()]
      .filter(Boolean)
      .join(" — ");
  }

  /* ── URL(?img=N)── 開いている間だけ付け、閉じたら消す。
     n/e など他のクエリには触らない(無限スクロールの表示URLと共存する)。 */
  function setUrlPage(n) {
    /* ★セクション設定「表示URLに ?n=&e= を付ける」を OFF にしたら、
       ?img= も止める(レビュー指摘 2026-08-14)。この設定は
       「クエリ付きURLの生成を止める緊急停止スイッチ」なので、
       新しく増やしたクエリも同じスイッチの下に置く。 */
    if (root.getAttribute("data-url-params") !== "true") return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set(URL_PARAM, String(n));
      history.replaceState(history.state, "", url);
    } catch (error) { /* URL API が無い環境では何もしない */ }
  }

  function clearUrlPage() {
    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has(URL_PARAM)) return;
      url.searchParams.delete(URL_PARAM);
      history.replaceState(history.state, "", url);
    } catch (error) { /* noop */ }
  }

  /* ── 表示 ── */

  function updateNav() {
    if (!prevButton || !nextButton) return;
    /* 1枚しか無い記事(ピラー等)ではボタン自体を出さない */
    var single = items.length <= 1;
    prevButton.hidden = single;
    nextButton.hidden = single;
    if (single) return;
    if (prShowing) {
      /* PR中はどちらへも抜けられる(閉じ込めない) */
      prevButton.disabled = false;
      nextButton.disabled = false;
      return;
    }
    /* ★端では hidden でなく disabled(レビュー指摘)。連打の最中にボタンが
       消えると、次のクリックが暗幕に落ちて「閉じる」になってしまう。 */
    prevButton.disabled = index <= 0;
    nextButton.disabled = index >= items.length - 1;
  }

  /* スマホの下部バーに出す「2 / 3」。PC では CSS で隠している。 */
  function updateCount() {
    if (!countLabel) return;
    countLabel.textContent = items.length > 1 && !prShowing
      ? (index + 1) + " / " + items.length
      : "";
  }

  function showImage(i) {
    var img = items[i];
    if (!img) return;
    index = i;
    prShowing = false;
    /* PR 状態クラスを外す(CSS 側が figure の表示/PRカードの中央寄せを切り替える) */
    box.classList.remove("gpm-lightbox--pr");
    if (prBox) { prBox.hidden = true; prBox.textContent = ""; }
    image.hidden = false;
    /* srcset は使わない。ここで欲しいのは常に一番大きい実体なので、
       レイアウト幅から候補を選ばれると拡大したのに解像度が上がらないことがある。 */
    image.src = img.currentSrc || img.src;
    image.alt = img.getAttribute("alt") || "";
    caption.textContent = captionFor(img);
    box.classList.remove("gpm-lightbox--wide");
    box.scrollTop = 0;
    setUrlPage(i + 1);
    updateNav();
    updateCount();
  }

  function showPr(fromIndex, targetIndex) {
    if (!prBox || !prTemplate) return false;
    var content = prTemplate.content && prTemplate.content.firstElementChild;
    if (!content) return false;
    prBox.textContent = "";
    prBox.appendChild(content.cloneNode(true));
    prBox.hidden = false;
    image.hidden = true;
    caption.textContent = "PR";
    prShowing = true;
    /* ★状態クラス(2026-08-29): CSS はこれを見て figure を丸ごと隠し、PR カードを
       画面中央に出す。img の hidden だけだと (1) display:block が UA の
       [hidden]{display:none} を上書きして画像が残る、(2) figure の margin:auto が
       余白を食ってカードが下端へ押し出される、の2つの崩れが起きていた。 */
    box.classList.add("gpm-lightbox--pr");
    prFrom = fromIndex;
    prTarget = targetIndex;
    prDir = targetIndex >= fromIndex ? 1 : -1;
    lastWasPr = true;
    box.scrollTop = 0;
    /* URLは変えない(PRはページではない。計測に混ぜない) */
    updateNav();
    updateCount();
    return true;
  }

  function advance(dir) {
    if (prShowing) {
      /* PRからの脱出は**進行方向**で決める(レビュー指摘 2026-08-14):
         「dir>0=行き先」と固定すると、**戻りながら**出たPRでは矢印の向きと
         実際の移動が逆転する(‹ を押したのに元のページへ戻される)。
         同じ方向をもう一度 → 元々の行き先 / 逆方向 → PRの前にいたページ。 */
      showImage(dir === prDir ? prTarget : prFrom);
      return;
    }
    var target = index + dir;
    if (target < 0 || target >= items.length) return;
    /* 切り替えの合間にランダムでPRを1枚はさむ(連続では出さない) */
    if (!lastWasPr && Math.random() < PR_CHANCE && showPr(index, target)) return;
    lastWasPr = false;
    showImage(target);
  }

  /* ── 開閉 ── */

  function open(img) {
    lastFocus = document.activeElement;
    /* ★範囲の決め方(2026-08-14):
       ・既定は**その話の中だけ**。無限スクロールで複数の話が同じ DOM に載るので、
         話をまたいで送ると読者が現在地を見失う。
       ・`data-gpm-gallery-all` が立っている記事(=「まんがだけ まとめて読む」)だけ、
         **ページ全体**を1つのギャラリーにする。最初の話の1枚目から
         最後の話の最終ページまで通しで送れる。 */
    var scope = root.hasAttribute("data-gpm-gallery-all")
      ? root
      : (img.closest(ENTRY_SEL) || root);
    items = Array.prototype.slice.call(scope.querySelectorAll(IMAGE_SEL));
    var at = items.indexOf(img);
    if (at < 0) { items = [img]; at = 0; }
    lastWasPr = false;
    box.hidden = false;
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    showImage(at);
    closeButton.focus();
  }

  function close() {
    if (box.hidden) return;
    box.hidden = true;
    box.classList.remove("gpm-lightbox--wide");
    box.classList.remove("gpm-lightbox--pr");
    if (prBox) { prBox.hidden = true; prBox.textContent = ""; }
    prShowing = false;
    image.hidden = false;
    /* src を空にしない。閉じるたびに捨てると、開き直すたび再取得になる。 */
    clearUrlPage();
    document.body.style.overflow = savedOverflow;
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  root.addEventListener("click", function (event) {
    var img = event.target.closest(IMAGE_SEL);
    if (!img || box.contains(img)) return;
    event.preventDefault();
    open(img);
  });

  /* キーボードでも開けるようにする。画像は本来フォーカスを持たないので、
     figure 側に tabindex を配って Enter / Space を拾う。 */
  root.addEventListener("keydown", function (event) {
    if (event.key !== "Enter" && event.key !== " ") return;
    var figure = event.target.closest(".gp-manga__page");
    if (!figure || box.contains(figure)) return;
    var img = figure.querySelector("img");
    if (!img) return;
    event.preventDefault();
    open(img);
  });

  /* 画像そのものを押したら「全体が入る」↔「幅いっぱい」を往復する。
     ★**スマホだけ**(2026-08-14 ユーザー指示「PCの画像拡大はいらない」)。
       PC は画面が広く、既定の表示で紙面が読める。PC で押しても何も起きないと
       固まったように見えるので、**PC では押した瞬間に閉じない**ことだけ守り、
       送りはドラッグ(下の pointer)とボタンとキーに任せる。 */
  image.addEventListener("click", function (event) {
    event.stopPropagation();
    if (dragSwiped) { dragSwiped = false; return; }
    if (!MOBILE.matches) return;
    box.classList.toggle("gpm-lightbox--wide");
    if (box.classList.contains("gpm-lightbox--wide")) box.scrollTop = 0;
  });

  if (prevButton) prevButton.addEventListener("click", function (event) {
    event.stopPropagation();
    advance(-1);
  });
  if (nextButton) nextButton.addEventListener("click", function (event) {
    event.stopPropagation();
    advance(1);
  });
  /* PRスライドの中(リンク)を押しても閉じない。背景(暗幕)だけが閉じる。 */
  if (prBox) prBox.addEventListener("click", function (event) {
    event.stopPropagation();
  });

  box.addEventListener("click", function () {
    /* ドラッグで送った直後の click は捨てる(はらったのに閉じてしまうため)。 */
    if (dragSwiped) { dragSwiped = false; return; }
    close();
  });
  closeButton.addEventListener("click", function (event) {
    event.stopPropagation();
    close();
  });
  document.addEventListener("keydown", function (event) {
    if (box.hidden) return;
    if (event.key === "Escape") { close(); return; }
    /* Alt+← はブラウザの「戻る」。修飾キー付きは奪わない(レビュー指摘)。 */
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    /* ★既定は ← が次・→ が前(まんがの読み方向。ボタン・フリックとそろえる)。
       LTR 面ではこれを反転する(→ が次)。 */
    if (event.key === "ArrowLeft") { event.preventDefault(); advance(LTR ? -1 : 1); }
    if (event.key === "ArrowRight") { event.preventDefault(); advance(LTR ? 1 : -1); }
  });

  /* ── はらって送る(指のフリック + PCのマウスドラッグ)──
     ★Pointer Events に**一本化**した(2026-08-14 ユーザー指示「PCでもフリックを」)。
       touch 系と mouse 系を別々に持つと、タッチ端末で両方が発火して二重に送る。
       pointer なら指もマウスもペンも同じ1本の道で来る。
     途中は追わず、始点と終点だけで判定する(スクロールを邪魔しない)。
     幅いっぱいモードの縦スクロールとは |dx| > |dy|×1.5 で棲み分ける。 */
  var dragX = 0, dragY = 0, dragging = false, dragSwiped = false;

  /* ★はらった直後の「幽霊クリック」を1回だけ握りつぶす(2026-08-30 追加)。
     PR の上でも指で送れるようにしたので、これが無いと「送ろうとしてはらったら
     広告のリンクが開いた」が起こり得る。送った瞬間に PR の中身は差し替わるため、
     PR 要素に付けた listener では間に合わない(リンクが DOM から外れてしまう)。
     そこで document の capture 段階で次の click を止める。指を動かしていない
     普通のタップでは呼ばれないので、CTA は従来どおり開く。 */
  function suppressNextClick() {
    var timer = null;
    function cleanup() {
      document.removeEventListener("click", kill, true);
      if (timer) clearTimeout(timer);
    }
    function kill(event) {
      event.preventDefault();
      event.stopPropagation();
      dragSwiped = false;
      cleanup();
    }
    document.addEventListener("click", kill, true);
    /* click が来ないまま終わる操作でも必ず後始末する。dragSwiped もここで落とす:
       残したままだと「はらったが click が合成されなかった」あと、次の意図的な
       タップが1回無視される(暗幕を押しても閉じない)。2026-08-30 修正。 */
    timer = setTimeout(function () { dragSwiped = false; cleanup(); }, 400);
  }

  box.addEventListener("pointerdown", function (event) {
    /* 右クリック・中クリックでは始めない。 */
    if (event.pointerType === "mouse" && event.button !== 0) return;
    /* ★PR カードの上でも**指もマウスも**送れる(2026-09-04 ユーザー指示)。
       2026-08-30 の時点では「ドラッグで文字を選びたい」という理由でマウスだけ
       対象外にしていたが、PC で広告カードの上をはらっても何も起きないのは
       操作が途切れて見えるという報告があったため、除外そのものを撤廃した。
       ・送りと判定するのは |dx| >= SWIPE_MIN(48px) かつ横が縦より明確に長い時だけ
         なので、文字を選ぶ程度の短いドラッグは従来どおり選択になる。
       ・はらった直後の「幽霊クリック」は suppressNextClick() が1回だけ握りつぶすので、
         広告のリンクが意図せず開くことはない(2026-08-30 の対策をそのまま使う)。 */
    dragging = true;
    dragSwiped = false;
    dragX = event.clientX;
    dragY = event.clientY;
  });

  box.addEventListener("pointerup", function (event) {
    if (!dragging) return;
    dragging = false;
    var dx = event.clientX - dragX;
    var dy = event.clientY - dragY;
    if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    /* ★**右へはらう=次のページ**。まんがは右から左へ読むので、
       紙の右綴じの本をめくるのと同じ向きにする。
       ボタン(左が次)・キー(←が次)と必ずそろえる。片方だけ直すと操作が混乱する。 */
    dragSwiped = true;      /* 直後の click(=閉じる/ズーム)を1回だけ食い止める */
    suppressNextClick();    /* PR のリンク等、要素側では拾えない幽霊クリックも止める */
    advance((LTR ? dx < 0 : dx > 0) ? 1 : -1);
  });

  box.addEventListener("pointercancel", function () { dragging = false; });

  /* 押せることが分かるように、ページ画像へ属性を配る。あとから増えた記事にも配る。 */
  function markImages() {
    Array.prototype.forEach.call(root.querySelectorAll(".gp-manga__page"), function (figure) {
      if (figure.hasAttribute("data-gpm-zoomable")) return;
      figure.setAttribute("data-gpm-zoomable", "");
      figure.setAttribute("tabindex", "0");
      figure.setAttribute("role", "button");
      figure.setAttribute("aria-label", "画像を大きく表示する");
    });
  }

  var main = root.querySelector(".gp-manga-article__main") || root;
  new MutationObserver(markImages).observe(main, { childList: true });
  markImages();

  /* ★共有URLやリロードで残った ?img= の掃除(レビュー指摘)。
     ライトボックスは閉じた状態で描画されるので、開いていないのに
     ?img= が付いたままだと「開いていない状態のURLに痕跡を残さない」
     という上の設計に反する。1回だけ消す。 */
  if (box.hidden) clearUrlPage();
})();
